/**
 * Skip-to-content focus management (WCAG 2.4.1 "Bypass Blocks").
 *
 * A bare `<a href="#main-content">` scrolls the target into view but does NOT
 * reliably move keyboard focus to it — a long-standing cross-browser quirk
 * (Safari/Firefox historically left focus on the link). Without focus actually
 * landing on `<main>`, the next Tab and the screen-reader cursor stay stranded
 * at the top of the page, defeating the whole point. In an SPA the bare anchor
 * also leaves a `#main-content` fragment on the URL.
 *
 * This composable returns a click handler that instead:
 *   1. cancels the native anchor jump (no URL fragment),
 *   2. programmatically focuses the landmark (so Tab + AT continue inside the
 *      content), defensively ensuring it can receive focus, and
 *   3. scrolls it into view, honouring reduced-motion (in-app toggle OR OS).
 *
 * The anchor keeps its `href` as the semantic/no-JS fallback.
 */
export interface UseSkipToContentReturn {
  /** Click/activation handler for the skip link. */
  skipToContent: (event?: Event) => void;
}

export function useSkipToContent(targetId = 'main-content'): UseSkipToContentReturn {
  function skipToContent(event?: Event): void {
    event?.preventDefault();

    if (typeof document === 'undefined') return;

    const target = document.getElementById(targetId);
    if (!target) return;

    // Guarantee the landmark is focusable even if the markup ever drops its
    // tabindex; a <main> is not focusable by default.
    if (!target.hasAttribute('tabindex')) {
      target.setAttribute('tabindex', '-1');
    }

    const prefersReducedMotion =
      document.documentElement.hasAttribute('data-reduced-motion') ||
      (typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true);

    // Focus without the implicit scroll, then scroll explicitly so we control
    // the motion behaviour. focus() is what actually moves the AT cursor.
    target.focus({ preventScroll: true });
    target.scrollIntoView?.({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  }

  return { skipToContent };
}
